import React, { useEffect, useReducer, useRef, useState } from "react";

import { Input, InputChangeEvent, SwitchChangeEvent } from "@progress/kendo-react-inputs";
import { getInitialState } from "@selas/state-management";
import { EntityEditor, handleChange, IAddEntityScreenProps, IEditEntityScreenProps, YesNoSwitch } from "@selas/ui-components";
import { isNullOrEmpty } from "@selas/utils";
import { useTranslation } from "react-i18next";

import Endpoint from "../../../services/api/endpoint";
import { sectorReducer } from "../../../state/reducers";
import { ISector } from "../../../utils/types/models";

const SectorEditor: React.FC<IAddEntityScreenProps<ISector> | IEditEntityScreenProps<ISector>> = (props: IAddEntityScreenProps<ISector> | IEditEntityScreenProps<ISector>) => {
	const { t } = useTranslation();

	const [state, dispatch] = useReducer(sectorReducer, getInitialState<ISector>());
	const initialSector: ISector = {
		id: (props as IEditEntityScreenProps<ISector>).recordId || 0,
		name: "",
		active: true
	};
	const [sector, setSector] = useState<ISector>(initialSector);
	const [dataChanged, setDataChanged] = useState<boolean>(false);
	const firstField: React.MutableRefObject<Input> = useRef();

	useEffect(() => {
		if (state.entity) {
			setSector(state.entity);
		}
	}, [state.entity]);

	function onChange(event: InputChangeEvent | SwitchChangeEvent): void {
		setSector(handleChange(sector, event));
		setDataChanged(true);
	}

	function getErrorMessages(): string[] {
		const messages: string[] = [];
		if (isNullOrEmpty(sector.name)) {
			messages.push(t("fill_in_required_field", { field: t("name").toLowerCase() }));
		}
		return messages;
	}

	function onActionButtonClicked(close: boolean, record: ISector): void {
		if (record && !close) {
			setSector(record);
			setDataChanged(false);
		}
		props.actionButtonClicked(close, record);
	}

	const readonly: boolean = (props as IEditEntityScreenProps<ISector>).readonly;

	return (
		<EntityEditor
			width="70%"
			record={sector}
			endpoint={Endpoint.Sectors}
			entityState={state}
			entityType={t("sector")}
			dispatch={dispatch}
			dataChanged={dataChanged}
			readonly={readonly}
			recordName={sector.name}
			actionButtonClicked={onActionButtonClicked}
			getErrorMessages={getErrorMessages}
			firstFieldRef={firstField}
		>
			<div className="k-form">
				<label className="k-form-field">
					<span>{t("name")} *</span>
					<Input name="name" ref={firstField} className="full-width-field" value={sector.name} onChange={onChange} required disabled={readonly} />
				</label>
				{!props.hideActive && (
					<div className="k-form-field">
						<div>{t("active")}</div>
						<YesNoSwitch name="active" checked={sector.active} onChange={onChange} disabled={readonly} />
					</div>
				)}
			</div>
		</EntityEditor>
	);
};

export default SectorEditor;
